import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Save, CheckCircle2, AlertCircle, Loader2 } from "lucide-react";
import { authHeader } from "@/hooks/useAdminAuth";
import API from "@/lib/api";
import { PAGE_SCHEMAS, PAGE_LABELS } from "./page-schemas";
import FieldRenderer from "./FieldRenderer";

export default function AdminPageEditor() {
  const { page, key } = useParams();
  const navigate = useNavigate();
  const pageSchemas = PAGE_SCHEMAS[page];
  const schema = pageSchemas?.[key];

  const [content, setContent] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState(null);

  useEffect(() => {
    if (!schema) return;
    setLoading(true);
    axios
      .get(`${API}/pages/${page}/sections/${key}`, { headers: authHeader() })
      .then(({ data }) => setContent(data.content || {}))
      .catch((err) =>
        setFeedback({ kind: "error", message: err.response?.data?.error || err.message })
      )
      .finally(() => setLoading(false));
  }, [page, key, schema]);

  if (!schema) {
    return (
      <div className="text-white">
        <p>Unknown section: <code>{page}/{key}</code></p>
        <Link to={`/ngxp-admin/pages/${page || "home"}`} className="text-[#00A8E8] underline">Back to sections</Link>
      </div>
    );
  }

  const setField = (name, value) => setContent((c) => ({ ...c, [name]: value }));

  const save = async (e) => {
    e?.preventDefault?.();
    setSaving(true);
    setFeedback(null);
    try {
      const { data } = await axios.put(
        `${API}/pages/${page}/sections/${key}`,
        { content },
        { headers: authHeader() }
      );
      setContent(data.content || {});
      setFeedback({ kind: "success", message: "Saved. Refresh the live page to see changes." });
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      setFeedback({ kind: "error", message: err.response?.data?.error || err.message });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-[#7B2CBF] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div>
      <div className="mb-6 flex items-start justify-between gap-4">
        <div>
          <button
            onClick={() => navigate(`/ngxp-admin/pages/${page}`)}
            className="inline-flex items-center gap-1.5 text-xs text-gray-500 hover:text-white transition-colors mb-2"
          >
            <ArrowLeft className="w-3.5 h-3.5" /> {PAGE_LABELS[page]} — All Sections
          </button>
          <h1 className="text-2xl font-bold text-white font-display">{schema.title}</h1>
          <p className="text-gray-500 mt-1 text-sm font-mono">{page} / {key}</p>
        </div>
        <button
          form="page-section-form"
          type="submit"
          disabled={saving}
          className="inline-flex items-center gap-2 px-5 py-2 rounded-lg text-sm font-semibold bg-gradient-to-r from-[#00A8E8] to-[#7B2CBF] text-white hover:opacity-90 disabled:opacity-50 transition-opacity"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          {saving ? "Saving..." : "Save Section"}
        </button>
      </div>

      {feedback && (
        <div
          className={`mb-5 flex items-start gap-2 px-4 py-3 rounded-lg border text-sm ${
            feedback.kind === "success"
              ? "bg-green-400/10 border-green-400/30 text-green-300"
              : "bg-red-400/10 border-red-400/30 text-red-300"
          }`}
        >
          {feedback.kind === "success" ? (
            <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0" />
          ) : (
            <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
          )}
          <span className="break-words">{feedback.message}</span>
        </div>
      )}

      <form id="page-section-form" onSubmit={save} className="space-y-5">
        {schema.fields.map((f) => (
          <FieldRenderer
            key={f.name}
            field={f}
            value={content[f.name]}
            onChange={(v) => setField(f.name, v)}
          />
        ))}

        <div className="pt-3 border-t border-white/5 flex justify-end">
          <button
            type="submit"
            disabled={saving}
            className="inline-flex items-center gap-2 px-5 py-2 rounded-lg text-sm font-semibold bg-gradient-to-r from-[#00A8E8] to-[#7B2CBF] text-white hover:opacity-90 disabled:opacity-50 transition-opacity"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            {saving ? "Saving..." : "Save Section"}
          </button>
        </div>
      </form>
    </div>
  );
}
